import type {
  CreateLinkInput,
  Link,
  UpdateLinkInput,
} from '@sunrise-ui/api/insights';
import { useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../constants/QueryKeys';
import { linksApi, useAuthQuery } from '../utils/api';
import { assertParameterIsNotEmptyString } from '../utils/error';

const { linksKey, byCommunityKey, byIdKey } = QueryKeys.links;

export const useGetLinks = (communityId?: string) => {
  const queryClient = useQueryClient();

  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response = await linksApi.listLinksLinksCommunitiesCommunityIdGet({
        communityId: communityId!,
      });

      // Cache each link individually for detail pages
      for (const link of response.data) {
        queryClient.setQueryData([linksKey, byIdKey, link.id], link);
      }

      return response.data;
    },
    queryKey: [linksKey, byCommunityKey, communityId],
    enabled: Boolean(communityId),
  });
};

export const useGetLinkById = (linkId?: string) => {
  return useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotEmptyString(linkId, 'linkId');
      const response = await linksApi.getLinkLinksLinkIdGet({
        linkId: linkId!,
      });
      return response.data;
    },
    queryKey: [linksKey, byIdKey, linkId],
    enabled: Boolean(linkId),
  });
};

export const useCreateLink = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<Link, unknown, { createLinkInput: CreateLinkInput }>({
    async mutationFn({ createLinkInput }) {
      const response = await linksApi.createLinkLinksPost({
        createLinkInput,
      });
      return response.data;
    },
    async onSuccess(link) {
      const { community_id } = link;
      await queryClient.invalidateQueries({
        queryKey: [linksKey, byCommunityKey, community_id],
      });

      showSnackbar({
        text: 'Shortened link created successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to create shortened link. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useUpdateLink = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    Link,
    unknown,
    { linkId: string; updateLinkInput: UpdateLinkInput }
  >({
    async mutationFn({ linkId, updateLinkInput }) {
      assertParameterIsNotEmptyString(linkId, 'linkId');
      const response = await linksApi.updateLinkLinksLinkIdPut({
        linkId,
        updateLinkInput,
      });
      return response.data;
    },
    async onSuccess(link) {
      const { community_id, id } = link;
      await queryClient.invalidateQueries({
        queryKey: [linksKey, byCommunityKey, community_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [linksKey, byIdKey, id],
      });

      showSnackbar({
        text: 'Shortened link updated successfully!',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update shortened link. Please try again.',
        type: 'error',
      });
    },
  });
};
